import { lockDocumentResizeCursor } from 'designSystem/components/lockDocumentResizeCursor'
import { SidePanelSide } from 'designSystem/components/sidePanelContext'
import { panelTrackWidthForPointer } from 'designSystem/components/sidePanelResize'
import {
  clampSidePanelWidth,
  resolveKeyboardPanelMaxWidth,
  writeSidePanelStoredWidth,
} from 'designSystem/components/sidePanelWidth'
import {
  type Dispatch,
  type KeyboardEvent,
  type MutableRefObject,
  type PointerEvent,
  type RefObject,
  type SetStateAction,
  useCallback,
} from 'react'

export function useSidePanelResize({
  draggingRef,
  exitRatioMode,
  maxWidth,
  minWidth,
  panelLayoutRef,
  persistStoredWidth,
  setWidth,
  side,
  skipNextOutsideClickRef,
  storageKey,
  width,
}: {
  draggingRef: MutableRefObject<boolean>
  exitRatioMode: () => void
  maxWidth: number | undefined
  minWidth: number
  panelLayoutRef: RefObject<HTMLDivElement | null>
  persistStoredWidth: boolean
  setWidth: Dispatch<SetStateAction<number>>
  side: SidePanelSide
  skipNextOutsideClickRef: MutableRefObject<boolean>
  storageKey?: string
  width: number
}): {
  handleResizeKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void
  handleResizePointerDown: (event: PointerEvent<HTMLDivElement>) => void
} {
  const commitWidth = useCallback(
    (next: number) => {
      const clamped = clampSidePanelWidth({ maxWidth, minWidth, width: next })
      setWidth(clamped)
      if (persistStoredWidth) writeSidePanelStoredWidth(clamped, storageKey)
    },
    [maxWidth, minWidth, persistStoredWidth, setWidth, storageKey]
  )

  const handleResizePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      const panelLayout = panelLayoutRef.current
      const handle = event.currentTarget
      if (!panelLayout) return

      const writeWidth = (clientX: number) => {
        const container = panelLayout.getBoundingClientRect()
        const nextWidth = clampSidePanelWidth({
          maxWidth,
          minWidth,
          width: panelTrackWidthForPointer({ clientX, container, side }),
        })
        panelLayout.style.setProperty('--sp-w', `${nextWidth}px`)
        return nextWidth
      }

      draggingRef.current = true
      panelLayout.dataset.panelResizing = 'true'
      handle.setPointerCapture(event.pointerId)
      const unlockCursor = lockDocumentResizeCursor()
      let nextWidth = writeWidth(event.clientX)
      setWidth(nextWidth)
      exitRatioMode()
      const pointerId = event.pointerId

      const onMove = (ev: globalThis.PointerEvent) => {
        nextWidth = writeWidth(ev.clientX)
      }

      const onUp = () => {
        document.removeEventListener('pointermove', onMove)
        document.removeEventListener('pointerup', onUp)
        document.removeEventListener('pointercancel', onUp)
        if (handle.hasPointerCapture(pointerId)) {
          handle.releasePointerCapture(pointerId)
        }
        unlockCursor()
        delete panelLayout.dataset.panelResizing
        draggingRef.current = false
        commitWidth(nextWidth)
        skipNextOutsideClickRef.current = true
      }

      document.addEventListener('pointermove', onMove)
      document.addEventListener('pointerup', onUp)
      document.addEventListener('pointercancel', onUp)
    },
    [
      commitWidth,
      draggingRef,
      exitRatioMode,
      maxWidth,
      minWidth,
      panelLayoutRef,
      setWidth,
      side,
      skipNextOutsideClickRef,
    ]
  )

  const handleResizeKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const dirSign = side === SidePanelSide.Right ? -1 : 1
      const step = event.shiftKey ? 64 : 16

      let next = width
      switch (event.key) {
        case 'ArrowLeft':
          next = width + -1 * dirSign * step
          break
        case 'ArrowRight':
          next = width + 1 * dirSign * step
          break
        case 'Home':
          next = minWidth
          break
        case 'End':
          next = resolveKeyboardPanelMaxWidth({
            containerWidth:
              panelLayoutRef.current?.getBoundingClientRect().width,
            maxWidth,
          })
          break
        default:
          return
      }

      event.preventDefault()
      exitRatioMode()
      commitWidth(next)
    },
    [
      commitWidth,
      exitRatioMode,
      maxWidth,
      minWidth,
      panelLayoutRef,
      side,
      width,
    ]
  )

  return { handleResizeKeyDown, handleResizePointerDown }
}
